import React from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import DataConnector from 'connect/DataConnector';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import * as NavConstants from 'constants/NavConstants';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {CrumbLink, SplitBreadcrumbs} from 'nav/Breadcrumbs';
import {
  ColumnAccessorKey,
  ColumnConfiguration,
  isWorklistItem,
  WorklistItem,
} from 'nav/expanded/ListingRendererConstants';
import ItemListing from 'nav/sidepanel/listing/ItemListing';
import * as SearchURLs from 'nav/sidepanel/listing/SearchURLs';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import notifications from 'notifications';
import * as GlobalSearchActions from 'search/GlobalSearchActions';
import {Filter, FilterKey, ListableItem, SearchParams, SearchType, SortKey} from 'search/SearchConstants';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import withSearching, {NavigationUpdate, SearchProps} from 'search/withSearching';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import {withLoadingScreen} from 'util/Promises';
import {BasicEvent} from 'util/Types';
import ScanIntoWorklistButton from 'worklists/ScanIntoWorklistButton';
import {dispatchUpdateWorklist} from 'worklists/WorklistActions';
import {WorklistSummary, WorklistTypeToModelTypes} from 'worklists/WorklistConstants';
import WorklistSettingsIcon from 'worklists/WorklistSettingsIcon';
import {resolveItemInWorklistFilter} from 'worklists/WorklistUtil';

export function maybeAddWorklistPositionColumn<T>(
  columns: Array<ColumnConfiguration<T>>,
  items: Array<T>
): void {
  if (items.length && isWorklistItem<T>(items[0])) {
    columns.splice(1, 0, {
      column: {
        header: getColumnHeaderFromSortKey(SortKey.SORT_WORKLIST_POSITION_KEY),
        accessorKey: ColumnAccessorKey.POSITION,
        isCollapsible: false,
        isSortable: true,
        sortKey: SortKey.SORT_WORKLIST_POSITION_KEY,
        width: 120,
      },
      render: (item: WorklistItem<T>) => <span className="u-ellipsis-text">{item.worklistItemPosition}</span>,
    });
  }
}

export interface WorklistItemsSidepanelProps {
  selectedWorklistId: string;
  clearSelectedWorklist: () => void;
  isExpanded: boolean;
  isExpanding: boolean;
  isCompactDisplay: boolean;
  setIsCompactDisplay: (isCompact: boolean) => void;
  activeItemId: string | null;
}

export interface EnhancedProps {
  worklistSummary: WorklistSummary | null;
  itemIds: Array<string> | null;
}

export class WorklistItemsSidepanelConnector extends DataConnector<
  WorklistItemsSidepanelProps & SearchProps,
  EnhancedProps
> {
  enhanceProps(): EnhancedProps {
    return {
      worklistSummary: this.getOrFetchOptional(
        ManagedModelType.WORKLIST_SUMMARIES_BY_ID,
        this.props.selectedWorklistId
      ),
      itemIds: this.getOrFetchOptional(
        ManagedModelType.WORKLIST_ITEMS_BY_WORKLIST_ID,
        this.props.selectedWorklistId
      ),
    };
  }
}

export function WorklistItemsSidepanel(
  props: WorklistItemsSidepanelProps & EnhancedProps & SearchProps
): JSX.Element {
  function handleSearch(searchParams: SearchParams): void {
    const newSearchParams = resolveItemInWorklistFilter(
      props.worklistSummary!.id,
      props.worklistSummary!.itemType,
      searchParams
    );
    props.onSearch(newSearchParams);
  }

  function handleUpdateFilters(filters: Array<Filter>): void {
    // TODO(Search): This should be the only event that we need to worry about adding the default
    // worklist filters to. The loading of a worklist should initialize the searchParams with them
    // by default, and the logic only depends on/mutates the filters themselves. This means that the
    // only way we would need to re-add them is if the filter is removed by ItemListing.
    // (Note that we still need to do this in handleSearch to cover cases like clearing params
    // as well.) All this said, this is baking a lot of assumptions about how WorklistItemsSidepanel
    // is loaded and used that aren't the best design. This seems to be a general flaw with this component
    // in that it assumes that it was loaded via a navigation action that pre-populates the searchParams.
    // We should improve the handling of these things as we continue down the listing consolidation path.
    const newSearchParams = resolveItemInWorklistFilter(
      props.worklistSummary!.id,
      props.worklistSummary!.itemType,
      {...props.searchParams, filters}
    );
    props.onUpdateFilters(newSearchParams.filters);
  }

  function decorateWithWorklistInfo(items: Array<ListableItem>): Array<ListableItem> {
    if (!props.itemIds) {
      return items;
    }
    const positions: {[id: string]: number} = {};
    for (let i = 0; i < props.itemIds.length; i++) {
      positions[props.itemIds[i]] = i + 1;
    }

    for (const item of items) {
      item.worklistItemPosition = positions[item.id];
    }

    if (props.worklistSummary) {
      for (const item of items) {
        item.worklistId = props.worklistSummary.id;
      }
    }

    return items;
  }

  async function getAllItemIdsInSortOrder(listedItemIds: Array<string>): Promise<Array<string>> {
    if (props.itemIds!.length > props.searchParams.limit) {
      // If the worklist contains more items than fit on one page, we need to fetch the full list in the
      // currently selected sort order
      const {ids} = await withLoadingScreen(
        GlobalSearchActions.doGlobalSearch(
          {...props.searchParams, limit: BenchlingConfig.WORKLISTS_MAX_ITEMS_PER_WORKLIST},
          SearchType.WORKLIST_ITEMS
        )
      );
      return ids;
    } else {
      return listedItemIds;
    }
  }

  async function onSaveOrder(listedItemIds: Array<string>): Promise<void> {
    if (!props.worklistSummary || !props.searchResults) {
      return;
    }
    assert(Boolean(props.itemIds), 'Save Order option should not appear when there are no items');
    assert(!props.searchParams.query, 'Save Order option should not appear when there is a search query');

    if (props.searchResults.total !== props.itemIds!.length) {
      notifications.error(
        'You cannot change the order of this worklist because it contains items that are inaccessible to you.'
      );
      return;
    }

    const allItemIdsInNewOrder = await getAllItemIdsInSortOrder(listedItemIds);
    dispatchUpdateWorklist(
      props.worklistSummary.id,
      {
        itemIds: allItemIdsInNewOrder,
      },
      {refreshWorklistItemsSearch: true}
    );
  }

  const crumbs = (
    <SplitBreadcrumbs
      crumbs={[
        <CrumbLink
          key="breadcrumbs-home"
          text="Home"
          onClick={(e: BasicEvent) => {
            e.preventDefault();
            SidepanelActions.openSection(NavConstants.Section.HOME);
          }}
        />,
        <CrumbLink
          key="breadcrumbs-worklists"
          text="Worklists"
          onClick={(e: BasicEvent) => {
            e.preventDefault();
            props.clearSelectedWorklist();
          }}
        />,
        props.worklistSummary ? (
          <CrumbLink key="root" text={props.worklistSummary.name} />
        ) : (
          <HorizontalThrobber key="root" />
        ),
      ]}
      settingsIcon={<WorklistSettingsIcon worklist={props.worklistSummary} itemIds={props.itemIds} />}
    />
  );

  return (
    <div className="sidepanel">
      <ItemListing
        {...props}
        actionSource={ActionOrigin.SIDEPANEL_HOME_WORKLISTS}
        onSearch={props.worklistSummary ? handleSearch : () => {}}
        onUpdateFilters={props.worklistSummary ? handleUpdateFilters : () => {}}
        onUpdateQueryString={props.worklistSummary ? props.onUpdateQueryString : () => {}}
        onUpdatePageSize={props.worklistSummary ? props.onUpdatePageSize : () => {}}
        onUpdateOffset={props.worklistSummary ? props.onUpdateOffset : () => {}}
        onUpdateSortOrder={props.worklistSummary ? props.onUpdateSortOrder : () => {}}
        onUpdateGroupBy={props.worklistSummary ? props.onUpdateGroupBy : () => {}}
        availableTypes={props.worklistSummary ? WorklistTypeToModelTypes[props.worklistSummary.itemType] : []}
        // eslint-disable-next-line react/no-unstable-nested-components -- Disabling for now, but worth fixing
        makeCrumbs={() => crumbs}
        extraPrimaryFilterOptions={[]}
        massageItems={decorateWithWorklistInfo}
        availableSortKeys={[
          SortKey.SORT_NAME_KEY,
          SortKey.SORT_IDENTIFIER_KEY,
          SortKey.SORT_MODIFIED_AT_KEY,
          SortKey.SORT_CREATED_AT_KEY,
        ]}
        preservedFilterKeys={[FilterKey.ITEM_IN_WORKLIST]}
        section={NavConstants.Section.WORKLIST_ITEMS}
        additionalHeaderButtons={
          props.worklistSummary && (
            <ScanIntoWorklistButton worklistSummary={props.worklistSummary} className="u-mini-spacing-left" />
          )
        }
        onSaveOrder={onSaveOrder}
        showMultiKeySortMenu={true}
      />
    </div>
  );
}

function getWorklistItemsURL(
  prevProps: WorklistItemsSidepanelProps & SearchProps,
  props: WorklistItemsSidepanelProps & SearchProps
): NavigationUpdate | null {
  if (!props.isExpanded) {
    return null;
  }
  return {url: SearchURLs.getNavigationURL('/worklist-items', props.searchParams, props.filterErrors)};
}

export default withSearching(
  WorklistItemsSidepanelConnector.connect(WorklistItemsSidepanel),
  SearchType.WORKLIST_ITEMS,
  NavConstants.Section.WORKLIST_ITEMS,
  {
    getNavigationUpdate: getWorklistItemsURL,
  }
);
